import {
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
} from 'react';

import type { ResourceDisplay } from '@aztec-realms/shared-types';

import { classNames } from './class-names';
import { ResourceIcon } from './icons';
import { buildSameOriginUrl, sameOriginRoute } from './safe-url';

type ButtonVariant = 'primary' | 'secondary' | 'danger';

export interface ActionButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  loading?: boolean;
}

export function ActionButton({
  variant = 'primary',
  loading = false,
  disabled,
  className,
  children,
  ...props
}: ActionButtonProps) {
  return (
    <button
      className={classNames('ar-button', `ar-button--${variant}`, className)}
      data-loading={loading || undefined}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <span aria-hidden="true" className="ar-button__loader" />}
      <span>{children}</span>
    </button>
  );
}

export function PrimaryActionButton(props: Omit<ActionButtonProps, 'variant'>) {
  return <ActionButton variant="primary" {...props} />;
}

export function SecondaryActionButton(props: Omit<ActionButtonProps, 'variant'>) {
  return <ActionButton variant="secondary" {...props} />;
}

export function DangerActionButton(props: Omit<ActionButtonProps, 'variant'>) {
  return <ActionButton variant="danger" {...props} />;
}

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
}

export function IconButton({ label, className, children, ...props }: IconButtonProps) {
  return (
    <button aria-label={label} className={classNames('ar-icon-button', className)} {...props}>
      {children}
    </button>
  );
}

type PanelVariant = 'stone' | 'jade' | 'parchment';

interface PanelProps extends HTMLAttributes<HTMLElement> {
  as?: 'article' | 'aside' | 'div' | 'section';
  variant?: PanelVariant;
  ornament?: 0 | 1 | 2;
}

export function Panel({
  as: Component = 'section',
  variant = 'stone',
  ornament = 1,
  className,
  ...props
}: PanelProps) {
  return (
    <Component
      className={classNames('ar-panel', `ar-panel--${variant}`, className)}
      data-ornament={ornament}
      {...props}
    />
  );
}

export function StonePanel(props: Omit<PanelProps, 'variant'>) {
  return <Panel variant="stone" {...props} />;
}

export function JadeGlassPanel(props: Omit<PanelProps, 'variant'>) {
  return <Panel variant="jade" {...props} />;
}

export function ParchmentPanel(props: Omit<PanelProps, 'variant'>) {
  return <Panel variant="parchment" {...props} />;
}

interface ResourceBarProps {
  resources: readonly ResourceDisplay[];
  compact?: boolean;
  loading?: boolean;
}

export function ResourceBar({ resources, compact = false, loading = false }: ResourceBarProps) {
  const formatter = new Intl.NumberFormat(undefined, {
    notation: compact ? 'compact' : 'standard',
  });

  if (resources.length === 0 && !loading) return null;

  return (
    <ul
      aria-busy={loading || undefined}
      aria-label={loading ? 'Resources loading' : 'Resources'}
      className="ar-resource-bar"
      data-compact={compact || undefined}
      data-loading={loading || undefined}
    >
      {loading ? (
        <li className="ar-resource-loading">
          <span aria-hidden="true" className="ar-resource-loading__marks">
            <i />
            <i />
            <i />
          </span>
          <span>Restoring resources</span>
        </li>
      ) : (
        resources.map((resource) => (
          <li
            className="ar-resource"
            key={resource.code}
            title={`${resource.label}: ${new Intl.NumberFormat().format(resource.amount)}`}
          >
            <span
              aria-hidden="true"
              className={`ar-resource__mark ar-resource__mark--${resource.code}`}
            >
              <ResourceIcon className="ar-resource__icon" resource={resource.code} />
            </span>
            <span className="ar-visually-hidden">{resource.label}: </span>
            <span className="ar-resource__value">{formatter.format(resource.amount)}</span>
          </li>
        ))
      )}
    </ul>
  );
}

interface QuestCardProps {
  eyebrow?: string;
  title: string;
  progressLabel?: string;
  href?: string;
  onNavigate?: (href: string) => void;
}

function navigateWithoutReload(
  event: MouseEvent<HTMLAnchorElement>,
  href: URL,
  onNavigate?: (href: string) => void,
) {
  if (
    !onNavigate ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }
  event.preventDefault();
  onNavigate(sameOriginRoute(href));
}

export function QuestCard({
  eyebrow = 'Current path',
  title,
  progressLabel,
  href,
  onNavigate,
}: QuestCardProps) {
  const safeHref = buildSameOriginUrl(href);
  const content = (
    <>
      <span className="ar-quest-card__eyebrow">{eyebrow}</span>
      <strong>{title}</strong>
      {progressLabel && <span>{progressLabel}</span>}
      {safeHref && (
        <span aria-hidden="true" className="ar-quest-card__arrow">
          →
        </span>
      )}
    </>
  );

  return safeHref ? (
    <a
      aria-label={[eyebrow, title, progressLabel].filter(Boolean).join(' · ')}
      className="ar-quest-card"
      href={safeHref.href}
      onClick={(event) => navigateWithoutReload(event, safeHref, onNavigate)}
    >
      {content}
    </a>
  ) : (
    <div className="ar-quest-card">{content}</div>
  );
}

interface RewardBundleProps {
  title?: string;
  resources: readonly ResourceDisplay[];
}

export function RewardBundle({ title = 'Reward preview', resources }: RewardBundleProps) {
  const titleId = useId();
  return (
    <section aria-labelledby={titleId} className="ar-reward-bundle">
      <h3 id={titleId}>{title}</h3>
      <ResourceBar resources={resources} />
    </section>
  );
}

interface ProgressMeterProps {
  label: string;
  value: number;
  max?: number;
}

export function ProgressMeter({ label, value, max = 100 }: ProgressMeterProps) {
  const safeMax = Math.max(max, 1);
  const safeValue = Math.min(Math.max(value, 0), safeMax);
  return (
    <div className="ar-progress-meter">
      <div className="ar-progress-meter__label">
        <span>{label}</span>
        <span className="ar-numeric">{Math.round((safeValue / safeMax) * 100)}%</span>
      </div>
      <progress aria-label={label} max={safeMax} value={safeValue}>
        {safeValue} of {safeMax}
      </progress>
    </div>
  );
}

interface CountdownTimerProps {
  label: string;
  remaining: string;
}

export function CountdownTimer({ label, remaining }: CountdownTimerProps) {
  return (
    <span className="ar-countdown">
      <span aria-hidden="true">◷</span>
      <span>{label}</span>
      <strong className="ar-numeric">{remaining}</strong>
    </span>
  );
}

interface SheetProps extends HTMLAttributes<HTMLElement> {
  title: string;
  open?: boolean;
  children: ReactNode;
}

export function BottomSheet({ title, open = true, children, className, ...props }: SheetProps) {
  const titleId = useId();
  if (!open) return null;
  return (
    <aside
      aria-labelledby={titleId}
      className={classNames('ar-sheet ar-bottom-sheet', className)}
      {...props}
    >
      <div aria-hidden="true" className="ar-sheet__handle" />
      <h2 id={titleId}>{title}</h2>
      {children}
    </aside>
  );
}

export function SideSheet({ title, open = true, children, className, ...props }: SheetProps) {
  const titleId = useId();
  if (!open) return null;
  return (
    <aside
      aria-labelledby={titleId}
      className={classNames('ar-sheet ar-side-sheet', className)}
      {...props}
    >
      <h2 id={titleId}>{title}</h2>
      {children}
    </aside>
  );
}

interface ModalProps {
  open: boolean;
  title: string;
  children: ReactNode;
  onClose: () => void;
}

export function Modal({ open, title, children, onClose }: ModalProps) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const closeRef = useRef(onClose);

  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    triggerRef.current = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeRef.current();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }

      const [first] = focusable;
      const last = focusable.at(-1);
      if (
        event.shiftKey &&
        (document.activeElement === first || document.activeElement === dialogRef.current)
      ) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      triggerRef.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="ar-modal-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div
        aria-labelledby={titleId}
        aria-modal="true"
        className="ar-modal"
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
      >
        <div className="ar-modal__header">
          <h2 id={titleId}>{title}</h2>
          <IconButton label="Close dialog" onClick={onClose}>
            ×
          </IconButton>
        </div>
        {children}
      </div>
    </div>
  );
}

interface ToastProps {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  tone?: 'info' | 'success' | 'warning';
}

export function Toast({ title, children, action, tone = 'info' }: ToastProps) {
  return (
    <div className="ar-toast" data-tone={tone} role="status">
      <div>
        <strong>{title}</strong>
        {children && <p>{children}</p>}
      </div>
      {action}
    </div>
  );
}
